import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, ArrowRight, Bell, CheckCircle2, Construction, Mail, Megaphone, Sparkles, Truck } from 'lucide-react';
const RELATED_SUGGESTIONS = [{
  id: 'heavy',
  name: 'Lowbed & Heavy Haulers',
  desc: 'For equipment, machinery & oversize loads',
  icon: Truck,
  accent: 'bg-amber-50 text-amber-700 border-amber-200'
}, {
  id: 'construction',
  name: 'Construction Haulers',
  desc: 'Tippers, flatbeds & material delivery',
  icon: Construction,
  accent: 'bg-orange-50 text-orange-700 border-orange-200'
}, {
  id: 'goods',
  name: 'Commercial Cargo Trucks',
  desc: 'Flatbeds, curtain-sides & box trucks',
  icon: Truck,
  accent: 'bg-blue-50 text-blue-700 border-blue-200'
}];
export function ServiceUnavailable() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as {
    prompt?: string;
    category?: string;
  };
  const category = state.category || 'crane services';
  const prompt = state.prompt || 'Move a crane to my construction site';
  const [contact, setContact] = useState('');
  const [notified, setNotified] = useState(false);
  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact.trim()) return;
    setNotified(true);
  };
  return <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-surface-100 to-white py-12 sm:py-16" data-id="element-2429">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2430">
        {/* Back link */}
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6 font-medium" data-id="element-2431">
          <ArrowLeft className="w-4 h-4" data-id="element-2432" /> Back
        </button>

        {/* Hero */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden mb-6" data-id="element-2433">
          <div className="relative px-6 sm:px-10 pt-10 pb-8 text-center bg-gradient-to-b from-amber-50/40 to-white" data-id="element-2434">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-100/60 blur-3xl pointer-events-none" data-id="element-2435" />
            <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-brand-100/40 blur-3xl pointer-events-none" data-id="element-2436" />

            <motion.div initial={{
            scale: 0.7,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            damping: 14,
            delay: 0.1
          }} className="relative inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-amber-100 text-amber-700 mb-5" data-id="element-2437">
              <AlertCircle className="w-10 h-10" strokeWidth={2} data-id="element-2438" />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl font-display font-black text-gray-900 mb-3 tracking-tight" data-id="element-2439">
              We're not in {category} yet
            </h1>
            <p className="text-base text-gray-600 max-w-xl mx-auto leading-relaxed" data-id="element-2440">
              No verified providers in our network match your request right now
              — but we're actively onboarding new categories. Tell us you're
              interested and we'll let you know the moment a carrier joins.
            </p>

            {prompt && <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-600 shadow-sm max-w-full" data-id="element-2441">
                <Sparkles className="w-3.5 h-3.5 text-brand-500 shrink-0" data-id="element-2442" />
                <span className="text-gray-400" data-id="element-2443">Your request:</span>
                <span className="font-semibold text-gray-800 truncate max-w-[28ch] sm:max-w-[40ch]" data-id="element-2444">
                  "{prompt}"
                </span>
              </div>}
          </div>

          {/* Notify-me form */}
          <div className="px-6 sm:px-10 py-7 border-t border-gray-100" data-id="element-2445">
            {!notified ? <>
                <div className="flex items-center gap-2 mb-3" data-id="element-2446">
                  <div className="w-9 h-9 rounded-xl orange-gradient-bg flex items-center justify-center" data-id="element-2447">
                    <Bell className="w-4 h-4 text-white" data-id="element-2448" />
                  </div>
                  <div data-id="element-2449">
                    <p className="font-bold text-gray-900" data-id="element-2450">Get notified</p>
                    <p className="text-xs text-gray-500" data-id="element-2451">
                      We'll email or SMS you when a verified {category} carrier
                      is live.
                    </p>
                  </div>
                </div>
                <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-2" data-id="element-2452">
                  <div className="relative flex-1" data-id="element-2453">
                    <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" data-id="element-2454" />
                    <input type="text" value={contact} onChange={e => setContact(e.target.value)} placeholder="email@example.com or +971…" className="w-full pl-10 pr-4 py-3 rounded-2xl border border-gray-200 outline-none text-sm focus:border-brand-300 font-medium" data-id="element-2455" />
                  </div>
                  <button type="submit" disabled={!contact.trim()} className="px-5 py-3 bg-gray-900 hover:bg-black text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-2456">
                    Notify me <ArrowRight className="w-4 h-4" data-id="element-2457" />
                  </button>
                </form>
              </> : <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-2xl p-4" data-id="element-2458">
                <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center shrink-0" data-id="element-2459">
                  <CheckCircle2 className="w-5 h-5 text-white" data-id="element-2460" />
                </div>
                <div data-id="element-2461">
                  <p className="font-bold text-green-800 text-sm" data-id="element-2462">
                    You're on the list
                  </p>
                  <p className="text-xs text-green-700" data-id="element-2463">
                    We'll reach out at <strong data-id="element-2464">{contact}</strong> as soon as a
                    verified provider joins.
                  </p>
                </div>
              </motion.div>}
          </div>
        </motion.div>

        {/* Custom request CTA */}
        <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} whileHover={{
        y: -2
      }} onClick={() => navigate('/marketplace')} className="w-full mb-8 group text-left bg-gray-900 hover:bg-black text-white rounded-3xl p-5 flex items-center gap-4 transition-colors shadow-lg" data-id="element-2465">
          <div className="w-12 h-12 rounded-2xl orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-2466">
            <Megaphone className="w-5 h-5 text-white" data-id="element-2467" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-2468">
            <p className="font-bold mb-0.5" data-id="element-2469">
              Post a custom request to the network
            </p>
            <p className="text-xs text-gray-300" data-id="element-2470">
              Verified haulers from adjacent categories may still bid on one-off
              jobs.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2471" />
        </motion.button>

        {/* Related suggestions */}
        <div className="mb-2 flex items-center justify-between" data-id="element-2472">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-2473">
            You might also need
          </p>
          <button onClick={() => navigate('/marketplace')} className="text-xs font-bold text-brand-600 hover:text-brand-700" data-id="element-2474">
            Browse all
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3" data-id="element-2475">
          {RELATED_SUGGESTIONS.map((s, i) => {
          const Icon = s.icon;
          return <motion.button key={s.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + i * 0.05
          }} whileHover={{
            y: -2
          }} onClick={() => navigate('/booking', {
            state: {
              service: s.id
            }
          })} className="text-left bg-white border border-gray-100 hover:border-gray-200 hover:shadow-md rounded-2xl p-4 transition-all group" data-id="element-2476">
                <div className={`inline-flex w-10 h-10 rounded-xl border items-center justify-center mb-3 ${s.accent}`} data-id="element-2477">
                  <Icon className="w-5 h-5" data-id="element-2478" />
                </div>
                <p className="font-bold text-gray-900 text-sm mb-1" data-id="element-2479">{s.name}</p>
                <p className="text-xs text-gray-500 leading-snug" data-id="element-2480">{s.desc}</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-bold text-gray-900" data-id="element-2481">
                  Browse <ArrowRight className="w-3.5 h-3.5" data-id="element-2482" />
                </div>
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}